import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Mail, Smartphone, Shield, Trash2, AlertTriangle, Info, Link2, CheckCircle2, Clock, Globe, Download, Loader2, BellRing } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import { exportAllDataZIP } from '../utils/exportUtils';
import { 
  isPushSupported, 
  isPushSubscribed, 
  subscribeToPush, 
  unsubscribeFromPush, 
  sendTestNotification,
  getNotificationPermission 
} from '../utils/pushNotifications';

export const Settings = () => {
  const navigate = useNavigate();
  const { t, language, changeLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [apps, setApps] = useState([]);
  const [connected, setConnected] = useState([]);
  const [exportingData, setExportingData] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [userTimezone, setUserTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [preferences, setPreferences] = useState({
    morning_notifications_enabled: false,
    evening_notifications_enabled: false,
    notification_method: 'in_app',
    morning_time: '10:00',
    evening_time: '20:00',
    push_notification_time: '09:00',
    push_notification_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Common timezones for dropdown
  const commonTimezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  const deleteConfirmWord = language === 'tr' ? 'SİL' : 'DELETE';

  // Download All Data handler
  const handleDownloadAllData = async () => {
    setExportingData(true);
    try {
      // Fetch all user data
      const [assessmentsRes, chatRes, profileRes] = await Promise.all([
        api.get('/assessment/history').catch(() => ({ data: { assessments: [] } })),
        api.get('/coaching/history').catch(() => ({ data: { messages: [] } })),
        api.get('/user/profile').catch(() => ({ data: {} })),
      ]);

      const userData = {
        assessments: assessmentsRes.data.assessments || [],
        chatHistory: chatRes.data.messages || [],
        profile: profileRes.data || {},
      };

      await exportAllDataZIP(userData, language);
      toast.success(language === 'tr' ? 'Verileriniz indirildi!' : 'Your data has been downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'tr' ? 'Dışa aktarma başarısız' : 'Export failed');
    } finally {
      setExportingData(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPreferences();
    fetchApps();
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    setPushSupported(isPushSupported());
    if (isPushSupported()) {
      const subscribed = await isPushSubscribed();
      setPushSubscribed(subscribed);
    }
  };

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        const result = await unsubscribeFromPush();
        if (result.success) {
          setPushSubscribed(false);
          toast.success(language === 'tr' ? 'Bildirimler devre dışı' : 'Notifications disabled');
        } else {
          toast.error(result.error || 'Failed to unsubscribe');
        }
      } else {
        const result = await subscribeToPush();
        if (result.success) {
          setPushSubscribed(true);
          toast.success(language === 'tr' ? 'Bildirimler etkinleştirildi!' : 'Notifications enabled!');
        } else {
          if (result.error === 'Permission denied') {
            toast.error(language === 'tr' ? 'Bildirim izni reddedildi' : 'Notification permission denied');
          } else {
            toast.error(result.error || 'Failed to subscribe');
          }
        }
      }
    } catch (error) {
      console.error('Push toggle error:', error);
      toast.error('Something went wrong');
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setPushLoading(true);
    try {
      const result = await sendTestNotification();
      if (result.success) {
        toast.success(language === 'tr' ? 'Test bildirimi gönderildi!' : 'Test notification sent!');
      } else {
        toast.error(result.error || 'Failed to send test notification');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setPushLoading(false);
    }
  };

  const fetchApps = async () => {
    try {
      const response = await api.get('/integrate/apps');
      setApps(response.data.apps);
      setConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to load apps:', error);
    }
  };

  const handleConnect = async (appId) => {
    try {
      const response = await api.post(`/integrate/connect/${appId}`);
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
      fetchApps();
    } catch (error) {
      toast.error(language === 'tr' ? 'Bağlantı başarısız' : 'Connection failed');
    }
  };

  const handleDisconnect = async (appId) => {
    try {
      await api.delete(`/integrate/disconnect/${appId}`);
      toast.success(language === 'tr' ? 'Bağlantı kesildi' : 'Disconnected');
      fetchApps();
    } catch (error) {
      toast.error(language === 'tr' ? 'Bağlantı kesilemedi' : 'Failed to disconnect');
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await api.get('/user/preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/user/preferences', preferences);
      toast.success(t('settings.saved'));
    } catch (error) {
      toast.error(t('settings.failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== deleteConfirmWord) {
      toast.error(language === 'tr' ? `Onaylamak için "${deleteConfirmWord}" yazın` : `Type "${deleteConfirmWord}" to confirm`);
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/user/delete-account');
      toast.success(language === 'tr' ? 'Hesabınız silindi' : 'Your account has been deleted');
      localStorage.removeItem('auth_token');
      navigate('/');
    } catch (error) {
      toast.error(language === 'tr' ? 'Hesap silinemedi' : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-16 px-6 pb-32 lg:pb-16">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans mb-8 flex items-center gap-2"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.backToDashboard')}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-2xl p-8"
        >
          <h2 className="font-serif font-light text-3xl text-[#F8F5F2] mb-8">
            {t('settings.title')}
          </h2>

          <div className="space-y-8">
            {/* Push Notifications */}
            {pushSupported && (
              <div className="space-y-4 p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                      <BellRing className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-sans text-lg text-[#F8F5F2] mb-0.5">
                        {language === 'tr' ? 'Günlük Hatırlatmalar' : 'Daily Reminders'}
                      </h3>
                      <p className="font-sans text-sm text-[#A1A1AA]">
                        {language === 'tr' 
                          ? 'Günlük afirmasyonlar ve görev hatırlatmaları alın' 
                          : 'Receive daily affirmations and mission reminders'}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pushSubscribed}
                      onChange={handlePushToggle}
                      disabled={pushLoading}
                      className="sr-only peer"
                      data-testid="push-notifications-toggle"
                    />
                    <div className={`w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#D4AF37]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37] ${pushLoading ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
                
                {pushSubscribed && (
                  <div className="pt-4 space-y-4 border-t border-white/10 mt-4">
                    {/* Notification Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                          {language === 'tr' ? 'Hatırlatma Saati' : 'Reminder Time'}
                        </label>
                        <input
                          type="time"
                          value={preferences.push_notification_time || '09:00'}
                          onChange={(e) => setPreferences({ ...preferences, push_notification_time: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none"
                          data-testid="push-notification-time"
                        />
                      </div>
                      <div>
                        <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                          {language === 'tr' ? 'Saat Dilimi' : 'Timezone'}
                        </label>
                        <select
                          value={preferences.push_notification_timezone || userTimezone}
                          onChange={(e) => setPreferences({ ...preferences, push_notification_timezone: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none"
                          data-testid="push-notification-timezone"
                        >
                          {commonTimezones.map((tz) => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Test Notification Button */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleTestNotification}
                        disabled={pushLoading}
                        className="px-4 py-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-sans hover:bg-[#D4AF37]/20 transition-colors disabled:opacity-50"
                        data-testid="test-notification-btn"
                      >
                        {pushLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          language === 'tr' ? 'Test Bildirimi Gönder' : 'Send Test Notification'
                        )}
                      </button>
                      <span className="text-xs text-[#6B7280]">
                        {language === 'tr' 
                          ? `Her gün ${preferences.push_notification_time || '09:00'} saatinde gönderilir` 
                          : `Sent daily at ${preferences.push_notification_time || '09:00'}`}
                      </span>
                    </div>
                  </div>
                )}
                
                {!pushSubscribed && getNotificationPermission() === 'denied' && (
                  <p className="text-xs text-red-400 pt-2">
                    {language === 'tr' 
                      ? 'Bildirim izni reddedildi. Tarayıcı ayarlarından izin verin.' 
                      : 'Notification permission denied. Please enable in browser settings.'}
                  </p>
                )}
              </div>
            )}

            {/* Morning Notifications */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-sans text-lg text-[#F8F5F2] mb-1">{t('settings.morningTitle')}</h3>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {t('settings.morningDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.morning_notifications_enabled}
                    onChange={(e) =>
                      setPreferences({ ...preferences, morning_notifications_enabled: e.target.checked })
                    }
                    className="sr-only peer"
                    data-testid="morning-notifications-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#D4AF37]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
                </label>
              </div>

              {preferences.morning_notifications_enabled && (
                <div className="pl-4 border-l-2 border-[#D4AF37]/30 space-y-3">
                  <div>
                    <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                      {t('settings.morningTime')}
                    </label>
                    <input
                      type="time"
                      value={preferences.morning_time}
                      onChange={(e) => setPreferences({ ...preferences, morning_time: e.target.value })}
                      className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-2 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
                      data-testid="morning-time-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Evening Check-IN */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-sans text-lg text-[#F8F5F2] mb-1">{t('settings.eveningTitle')}</h3>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {t('settings.eveningDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.evening_notifications_enabled}
                    onChange={(e) =>
                      setPreferences({ ...preferences, evening_notifications_enabled: e.target.checked })
                    }
                    className="sr-only peer"
                    data-testid="evening-notifications-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#D4AF37]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
                </label>
              </div>

              {preferences.evening_notifications_enabled && (
                <div className="pl-4 border-l-2 border-[#D4AF37]/30 space-y-3">
                  <div>
                    <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                      {t('settings.eveningTime')}
                    </label>
                    <input
                      type="time"
                      value={preferences.evening_time}
                      onChange={(e) => setPreferences({ ...preferences, evening_time: e.target.value })}
                      className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-2 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
                      data-testid="evening-time-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Notification Method */}
            <div className="space-y-4">
              <h3 className="font-sans text-lg text-[#F8F5F2]">{t('settings.notificationMethod')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => setPreferences({ ...preferences, notification_method: 'in_app' })}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    preferences.notification_method === 'in_app'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid="notification-method-in-app"
                >
                  <Smartphone className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="font-sans text-sm text-[#F8F5F2]">{t('settings.inApp')}</p>
                </button>

                <button
                  onClick={() => setPreferences({ ...preferences, notification_method: 'email' })}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    preferences.notification_method === 'email'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid="notification-method-email"
                >
                  <Mail className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="font-sans text-sm text-[#F8F5F2]">{t('settings.email')}</p>
                </button>

                <button
                  onClick={() => setPreferences({ ...preferences, notification_method: 'both' })}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    preferences.notification_method === 'both'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid="notification-method-both"
                >
                  <Bell className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="font-sans text-sm text-[#F8F5F2]">{t('settings.both')}</p>
                </button>
              </div>
            </div>

            {/* Language Selection */}
            <div className="space-y-4">
              <div>
                <h3 className="font-sans text-lg text-[#F8F5F2] mb-1 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#D4AF37]" />
                  {language === 'tr' ? 'Dil / Language' : 'Language / Dil'}
                </h3>
                <p className="font-sans text-sm text-[#A1A1AA]">
                  {language === 'tr' ? 'Uygulama dilini seçin' : 'Choose your preferred language'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    language === 'en'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid="language-english"
                >
                  <span className="text-2xl mb-2 block">🇬🇧</span>
                  <p className="font-sans text-sm text-[#F8F5F2]">English</p>
                </button>
                <button
                  onClick={() => changeLanguage('tr')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    language === 'tr'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid="language-turkish"
                >
                  <span className="text-2xl mb-2 block">🇹🇷</span>
                  <p className="font-sans text-sm text-[#F8F5F2]">Türkçe</p>
                </button>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans disabled:opacity-50"
              data-testid="save-preferences-button"
            >
              {saving ? t('settings.saving') : t('settings.savePreferences')}
            </button>
          </div>
        </motion.div>

        {/* Connected Apps Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-8 mt-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <Link2 className="w-6 h-6 text-[#D4AF37]" />
            <h2 className="font-serif font-light text-2xl text-[#F8F5F2]">
              {language === 'tr' ? 'Bağlı Uygulamalar' : 'Connected Apps'}
            </h2>
          </div>

          <p className="font-sans text-sm text-[#A1A1AA] mb-6">
            {language === 'tr' 
              ? 'Sağlık ve fitness uygulamalarınızı bağlayarak verilerinizi senkronize edin.' 
              : 'Connect your health and fitness apps to sync your data.'}
          </p>

          {connected.length > 0 && (
            <div className="mb-6 p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
              <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-3">
                {language === 'tr' ? 'Aktif Bağlantılar' : 'Active Connections'}
              </p>
              <div className="flex flex-wrap gap-2">
                {connected.map((app) => (
                  <div
                    key={app.app_name}
                    className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-3 py-1.5"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="font-sans text-[#F8F5F2] text-sm capitalize">
                      {app.app_name.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {apps.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between p-4 rounded-xl border border-white/10 hover:border-[#D4AF37]/20 transition-all"
                data-testid={`app-${app.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-3xl">{app.icon}</div>
                  <div>
                    <h4 className="font-sans text-[#F8F5F2] font-medium">{app.name}</h4>
                    <p className="font-sans text-xs text-[#A1A1AA]">{app.description}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {app.domains.slice(0, 3).map((domain) => (
                        <span
                          key={domain}
                          className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-1.5 py-0.5 rounded-full font-sans"
                        >
                          {domain}
                        </span>
                      ))}
                      {app.domains.length > 3 && (
                        <span className="text-[10px] text-[#A1A1AA] px-1">+{app.domains.length - 3}</span>
                      )}
                    </div>
                  </div>
                </div>

                {app.status === 'coming_soon' ? (
                  <button
                    disabled
                    className="rounded-full border border-[#D4AF37]/20 text-[#A1A1AA] px-4 py-1.5 font-sans text-xs flex items-center gap-1.5 cursor-not-allowed"
                  >
                    <Clock className="w-3 h-3" />
                    {language === 'tr' ? 'Yakında' : 'Soon'}
                  </button>
                ) : app.connected ? (
                  <button
                    onClick={() => handleDisconnect(app.id)}
                    className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-1.5 hover:bg-[#D4AF37]/20 transition-all font-sans text-xs"
                    data-testid={`disconnect-${app.id}`}
                  >
                    {language === 'tr' ? 'Bağlantıyı Kes' : 'Disconnect'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(app.id)}
                    className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-1.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans text-xs"
                    data-testid={`connect-${app.id}`}
                  >
                    {language === 'tr' ? 'Bağlan' : 'Connect'}
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="font-sans text-xs text-[#6B7280] mt-4 text-center">
            {language === 'tr' 
              ? 'Tam API entegrasyonları yakında geliyor. Şu an için simüle edilmiş veri kullanılıyor.' 
              : 'Full API integrations coming soon. Currently using simulated data.'}
          </p>
        </motion.div>

        {/* Privacy & Data Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-8 mt-8"
        >
          <h2 className="font-serif font-light text-2xl text-[#F8F5F2] mb-6">
            {language === 'tr' ? 'Gizlilik ve Verilerim' : 'Privacy & My Data'}
          </h2>

          <div className="space-y-6">
            {/* About Link */}
            <button
              onClick={() => navigate('/about')}
              className="w-full flex items-center justify-between p-4 rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all group"
              data-testid="about-button"
            >
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-[#D4AF37]" />
                <div className="text-left">
                  <p className="font-sans text-[#F8F5F2]">
                    {language === 'tr' ? 'IN Hakkında' : 'About IN'}
                  </p>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {language === 'tr' ? 'Felsefe, misyon ve yaratıcı' : 'Philosophy, mission & creator'}
                  </p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#D4AF37] rotate-180 transition-colors" />
            </button>

            {/* Privacy Policy Link */}
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between p-4 rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all group"
              data-testid="privacy-policy-button"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
                <div className="text-left">
                  <p className="font-sans text-[#F8F5F2]">
                    {language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}
                  </p>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {language === 'tr' ? 'Verilerinizi nasıl koruduğumuzu öğrenin' : 'Learn how we protect your data'}
                  </p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#D4AF37] rotate-180 transition-colors" />
            </button>

            {/* Download All My Data */}
            <div className="border-t border-[#D4AF37]/20 pt-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <Download className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <div>
                  <p className="font-sans text-[#F8F5F2] font-medium">
                    {language === 'tr' ? 'Verilerimi İndir' : 'Download My Data'}
                  </p>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {language === 'tr' 
                      ? 'Tüm Sun Map sonuçlarınızı, AI analizlerinizi ve koçluk sohbetlerinizi ZIP dosyası olarak indirin.'
                      : 'Download all your Sun Map results, AI analyses, and coaching chats as a ZIP file.'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDownloadAllData}
                disabled={exportingData}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-all font-sans disabled:opacity-50"
                data-testid="download-all-data-button"
              >
                {exportingData ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {language === 'tr' ? 'Hazırlanıyor...' : 'Preparing...'}
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    {language === 'tr' ? 'Tüm Verilerimi İndir (ZIP)' : 'Download All My Data (ZIP)'}
                  </>
                )}
              </button>
            </div>

            {/* Delete Account */}
            <div className="border-t border-red-500/20 pt-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="font-sans text-red-400 font-medium">
                    {language === 'tr' ? 'Tehlikeli Bölge' : 'Danger Zone'}
                  </p>
                  <p className="font-sans text-sm text-[#A1A1AA]">
                    {language === 'tr' 
                      ? 'Bu işlem geri alınamaz. Tüm verileriniz kalıcı olarak silinecektir.'
                      : 'This action cannot be undone. All your data will be permanently deleted.'}
                  </p>
                </div>
              </div>

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-all font-sans"
                  data-testid="delete-account-button"
                >
                  <Trash2 className="w-5 h-5" />
                  {language === 'tr' ? 'Hesabımı ve Tüm Verilerimi Sil' : 'Delete My Account & All Data'}
                </button>
              ) : (
                <div className="space-y-4 p-4 rounded-xl border border-red-500/30 bg-red-500/5">
                  <p className="font-sans text-[#F8F5F2]">
                    {language === 'tr' 
                      ? `Onaylamak için "${deleteConfirmWord}" yazın:`
                      : `Type "${deleteConfirmWord}" to confirm:`}
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={deleteConfirmWord}
                    className="w-full bg-white/5 border border-red-500/30 rounded-lg px-4 py-2 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-red-500/50 focus-visible:outline-none"
                    data-testid="delete-confirm-input"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="flex-1 p-3 rounded-lg border border-[#D4AF37]/30 text-[#A1A1AA] hover:text-[#F8F5F2] transition-all font-sans"
                    >
                      {language === 'tr' ? 'İptal' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirmText !== deleteConfirmWord}
                      className="flex-1 p-3 rounded-lg border border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all font-sans disabled:opacity-50"
                      data-testid="confirm-delete-button"
                    >
                      {deleting 
                        ? (language === 'tr' ? 'Siliniyor...' : 'Deleting...') 
                        : (language === 'tr' ? 'Kalıcı Olarak Sil' : 'Delete Permanently')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
      <BottomNav />
    </div>
  );
};

export default Settings;
